"use client";

import { useEffect, useRef, useState } from "react";
import {
  ActionMenu,
  ActionMenuItem,
  COMPACT_ONLY,
  HOVER_ROW_ONLY,
} from "@/components/ui/ActionMenu";
import { popoverBoundsRect } from "@/lib/anchoredPosition";
import { Avatar } from "@/components/ui/Avatar";
import { BotBadge } from "@/components/BotBadge";
import { BotTranscriptButton } from "@/components/BotTranscriptButton";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmojiReactionPicker } from "@/components/EmojiReactionPicker";
import { AttachedFiles } from "@/components/AttachedFiles";
import { ImageLightbox } from "@/components/ImageLightbox";
import { RichContent } from "@/components/RichContent";
import {
  RichTextInput,
  type RichTextInputHandle,
} from "@/components/RichTextInput";
import { SubmitTooltip } from "@/components/SubmitShortcut";
import { TimeAgo, EditedLabel } from "@/components/TimeAgo";
import { readyAttachments, videoAttachments } from "@/lib/attachments";
import { cookedToPlainText, extractAndStripImages } from "@/lib/processCooked";
import type { Message, MessageReaction } from "@/lib/api/messages";
import {
  useDeleteMessage,
  useEditMessage,
  useReactToMessage,
} from "@/lib/hooks/useConversation";
import { DmReactionSummary } from "./DmReactionSummary";
import { QuotedMessage } from "./MessageQuote";
import { ReportDialog } from "./ReportDialog";
import { personName } from "./participants";

/**
 * THE SYSTEM LINES, IN DANISH. `actionCode` picks the sentence; the two
 * arguments are everything a sentence may say.
 *
 * `name` is the line's SUBJECT, not necessarily whoever did it (quirk Q20):
 * "<name> blev tilføjet til samtalen" names the newcomer, and the row's author
 * is the newcomer's.
 *
 * `text` is the line's one variable and it comes OFF THE MESSAGE — OSO-549. It
 * is never read from the conversation the line is sitting in: rename a thread
 * twice and both lines would then claim the newest name, which is the opposite
 * of what a history is for. `null` on `renamed_conversation` is meaningful
 * rather than missing — it is the CLEAR, and gets its own sentence.
 */
const SMALL_ACTION_TEXT: Record<
  string,
  (name: string, text: string | null) => string
> = {
  user_left: (name) => `${name} forlod samtalen`,
  removed_user: (name) => `${name} blev fjernet fra samtalen`,
  invited_user: (name) => `${name} blev tilføjet til samtalen`,
  renamed_conversation: (name, text) =>
    text
      ? `${name} ændrede samtalens navn til "${text}"`
      : `${name} fjernede samtalens navn`,
};

/**
 * THE SAME ACTIONS, AS A ⋮, WHERE THERE IS NO HOVER TO WAIT FOR (OSO-660).
 *
 * The two class strings moved to `ui/ActionMenu` when the document row needed
 * exactly the same split (OSO-688); their header there is the long version.
 *
 * REACTING IS NOT IN THE MENU. It is the most-used of the five and the only one
 * that opens a picker of its own, so behind the ⋮ it would cost two taps and a
 * popover fighting the menu it came out of. It stays a direct affordance on
 * both layouts — one icon plus a ⋮ is still half the row that was reported.
 */

/**
 * The lightbox sidebar for a message. A DM has no post panel — no reactions,
 * no comments — so this is just who sent it, when, and what they wrote. The
 * images are already on screen, so the caption drops them and keeps the words.
 */
function MessageLightboxSidebar({ message }: { message: Message }) {
  const caption = cookedToPlainText(
    extractAndStripImages(message.bodyHtml ?? "").html,
  );

  return (
    <div className="px-4 pt-4 pb-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
      <div className="flex items-center gap-2.5">
        <Avatar
          url={message.author?.avatar.small}
          name={personName(message.author)}
          size="sm"
        />
        <div>
          <p className="font-semibold text-sm text-foreground">
            {personName(message.author)}
          </p>
          <TimeAgo date={message.createdAt} />
        </div>
      </div>

      {caption && (
        <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
          {caption}
        </p>
      )}
    </div>
  );
}

/**
 * REWRITING A MESSAGE, IN PLACE — OSO-607.
 *
 * It sits INSIDE the bubble and inherits its colour, so the words stay exactly
 * where they were and the thread does not reflow around a form. That is why it
 * is not `CommentEditor`: that one is a block under a comment and, more to the
 * point, it edits attachments and posts through `updatePost`. A message's
 * attachments are not editable (`PATCH /messages/{id}` takes only the text),
 * so there is no tray, no upload button and no scope to thread through.
 *
 * `RichTextInput` and not a bare `<textarea>` — mentions, the emoji
 * autocomplete and ⌘+Enter are the shared primitive's, and a message being
 * edited must behave like a message being written.
 */
function MessageEditor({
  inputRef,
  value,
  onChange,
  pending,
  failed,
  onSave,
  onCancel,
}: {
  inputRef: React.RefObject<RichTextInputHandle | null>;
  value: string;
  onChange: (value: string) => void;
  pending: boolean;
  failed: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <RichTextInput
        ref={inputRef}
        ariaLabel="Redigér beskeden"
        value={value}
        onChange={onChange}
        multiline
        autoFocus
        onSubmit={onSave}
        onEscape={onCancel}
        hideFormattingIcon
        placeholder="Redigér beskeden"
        className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-vertical"
      />
      {failed && (
        <p className="text-xs text-destructive bg-surface rounded px-1">
          Beskeden kunne ikke gemmes. Prøv igen.
        </p>
      )}
      <div className="flex items-center gap-2">
        <SubmitTooltip label="Gem ændringer" className="relative">
          <button
            type="button"
            onClick={onSave}
            disabled={pending}
            className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {pending ? "Gemmer…" : "Gem"}
          </button>
        </SubmitTooltip>
        <button
          type="button"
          onClick={onCancel}
          className="px-2.5 py-1 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          Annuller
        </button>
      </div>
    </div>
  );
}

export function MessageBubble({
  message,
  showAuthor,
  showTimestamp = true,
  conversationId,
  canReport = false,
  onReply,
  onReacted,
}: {
  message: Message;
  /** Show avatar + name above the bubble (first message of a run, or a group). */
  showAuthor: boolean;
  /** Show the per-message timestamp (first message of a run, or a big enough
   * gap since the previous one — collapsed otherwise so a run of quick
   * messages doesn't repeat the same stamp on every bubble). */
  showTimestamp?: boolean;
  /**
   * Addresses the report dialog, and scopes the emoji picker's custom-emoji
   * library to the groups this conversation's participants share. Reacting
   * itself no longer needs it — a message id is the whole address (D10b).
   */
  conversationId?: number;
  /**
   * `conversation.can.report`, from the document that carries it. NOT
   * recomputed here: whether a report can be filed depends on a shared group,
   * on there being no duplicate, and on the evidence rule — three things only
   * the server knows.
   */
  canReport?: boolean;
  /** Start composing a reply that quotes this message. */
  onReply?: (message: Message) => void;
  /**
   * The authoritative pill list after a reaction, handed back to whoever owns
   * the message list. The thread splices it: a reacted message can sit in a
   * page older than the polled window, where invalidating a query would not
   * reach it, and the server's summary is not something to recompute.
   */
  onReacted?: (messageId: number, reactions: MessageReaction[]) => void;
}) {
  const [reportOpen, setReportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  /**
   * THE SAVED MESSAGE, HELD UNTIL THE POLL CATCHES UP (OSO-607). An edit moves
   * `max(updated_at)`, which is in the thread's conditional token, so the new
   * words reach everybody — including this reader — within a tick. A tick is
   * long enough to see the OLD text sitting under a "Gem" that has already
   * succeeded, which reads as a failure, so the server's own answer is painted
   * immediately and superseded by the poll. Same move `CommentItem` makes with
   * `localBodyHtml`.
   */
  const [saved, setSaved] = useState<Message | null>(null);
  const draftRef = useRef<RichTextInputHandle>(null);
  /** The action cluster, plus the ⋮ menu's open state and which side its panel
   *  opens into — both measured below, which is why the menu is controlled
   *  rather than self-owned. */
  const actionsRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAlign, setMenuAlign] = useState<"left" | "right">("right");
  const react = useReactToMessage();
  const remove = useDeleteMessage();
  const edit = useEditMessage();

  /**
   * WHICH SIDE THE PANEL OPENS INTO — MEASURED, NOT READ OFF `mine`.
   *
   * The first version of this took `align="left"` for an own message and the
   * default `right` for everyone else, reasoning from which side of the thread
   * the bubble is on. That is the wrong variable. An own message is
   * `justify-end`, so on a SHORT one — "Ok", "Ja", "👍" — the cluster sits hard
   * against the right edge and a left-aligned panel hangs off it. Measured in
   * Chromium at 375×600: 19px past the scroller on `mine` + "Ok", while the
   * same alignment on a long own message has 207px to spare.
   *
   * So the panel opens into whichever side has more room, which needs no
   * knowledge of how wide the panel is and can therefore be decided from the
   * trigger alone, BEFORE the panel exists. `popoverBoundsRect` is the shared
   * answer to "what box must a popover stay inside" — the thread's scroller
   * here (`ConversationThread:440`), the dock's list in the dock, the viewport
   * when there is neither. Measured over the four cases, the rule fits every
   * one and picks the empty side of the row rather than the side the bubble is
   * already on:
   *
   *     mineShort    left → 19px over  |  rule: right, 71px to spare
   *     mineLong     left → fits       |  rule: left, unchanged
   *     theirsShort  right → fits      |  rule: left, opens away from the avatar
   *     theirsLong   right → fits      |  rule: right, unchanged
   */
  const chooseMenuAlign = (): "left" | "right" => {
    const trigger = actionsRef.current?.querySelector('[aria-haspopup="menu"]');
    if (!trigger) return "right";
    const t = trigger.getBoundingClientRect();
    const bounds = popoverBoundsRect(trigger);
    // What a left-aligned panel grows into, against what a right-aligned one does.
    return bounds.left + bounds.width - t.left >= t.right - bounds.left ? "left" : "right";
  };

  const handleMenuOpenChange = (open: boolean) => {
    // Measured before the panel is mounted, so both updates land in one render
    // and it never paints on the wrong side and then jumps.
    if (open) setMenuAlign(chooseMenuAlign());
    setMenuOpen(open);
  };

  /**
   * THE ⋮ PANEL IS PULLED ONTO THE SCREEN WHEN IT OPENS (OSO-660).
   *
   * `ActionMenu`'s panel is `absolute top-7` and does not flip — its header
   * says so, and says that folding anchored positioning in would make every ⋮
   * on the platform carry it so one caller could have it. On the NEWEST
   * message that matters in a way it does not for a comment: the thread opens
   * scrolled to the bottom (`ConversationThread:224`), so the last bubble sits
   * at the fold. Measured in Chromium at 375×600 with the thread at the
   * bottom: of a three-row panel's 103px, 86px land below the scroller and a
   * 17px sliver shows.
   *
   * It is NOT clipped. The panel's containing block is inside the scroller, so
   * it extends `scrollHeight` (583 → 669) and a swipe reaches it — and a touch
   * pan cannot dismiss it, because `useDismissable` listens on `mousedown` and
   * a scrolling gesture produces none. But "swipe at a 17px sliver" is not an
   * affordance, and before this change that reader had four working buttons.
   *
   * VERTICAL ONLY, AND BY HAND. `scrollIntoView({ block: "nearest" })` did
   * this correctly and then did one thing more: `inline` defaults to
   * `"nearest"` too, the scroller's `overflow-x` computes to `auto`, and with
   * the mis-aligned panel above hanging 19px off the right edge it scrolled
   * `scrollLeft` 0 → 19 — sliding every message in the thread sideways, with
   * no scrollbar to explain why. The option to decline the inline axis does
   * not exist, so the vertical delta is computed against the bounds box and
   * written to `scrollTop`, which is the only axis that should ever move.
   *
   * `ConversationThread`'s content observer cannot do this job — it watches
   * that div's box, and an out-of-flow panel does not change it (checked: it
   * does not fire).
   *
   * NOT `panelClassName="top-auto bottom-7"` to open upward for everybody: a
   * scroller's scrollable region excludes block-START overflow, so on the
   * oldest message the panel would sit 83px above the top with `scrollHeight`
   * unchanged and `scrollTop` already 0 — unreachable, which is worse than
   * below the fold.
   */
  useEffect(() => {
    if (!menuOpen) return;
    const panel = actionsRef.current?.querySelector('[role="menu"]');
    const scroller = actionsRef.current?.closest<HTMLElement>("[data-popover-bounds]");
    if (!panel || !scroller) return;
    const p = panel.getBoundingClientRect();
    const s = scroller.getBoundingClientRect();
    const below = p.bottom - s.bottom;
    const above = s.top - p.top;
    if (below > 0) scroller.scrollTop += below;
    else if (above > 0) scroller.scrollTop -= above;
  }, [menuOpen]);

  // System line — "Niels forlod samtalen". Never a bubble.
  if (message.kind === "system") {
    const name = personName(message.author);
    const build = message.actionCode
      ? SMALL_ACTION_TEXT[message.actionCode]
      : undefined;
    const text = build
      ? build(name, message.actionText)
      : `${name} opdaterede samtalen`;
    return (
      <p className="text-xs text-subtle-foreground py-2 text-center">{text}</p>
    );
  }

  const mine = message.isMine;
  const authorName = personName(message.author);
  const bodyHtml = saved?.bodyHtml ?? message.bodyHtml;
  const editedAt = saved?.editedAt ?? message.editedAt;

  /**
   * ONE `attachments` ARRAY, WITH RENDITIONS (C16/U11) — no second `image_urls`
   * list that could disagree with it about how many images a message has, and
   * no markdown token to parse back out of the body (§1.12).
   *
   * These URLs are SIGNED AND TIME-LIMITED, because every DM attachment is
   * protected tier. They are read straight off the message the query holds and
   * must never be copied anywhere that outlives it.
   *
   * `readyAttachments` is the other half of that sentence (OSO-490): a clip
   * still encoding has no signed url yet, and only its SENDER is ever handed
   * one. Images stay narrowed to the ready ones for that reason (so do audio
   * and files, inside `AttachedFiles`); videos do not, because a non-ready
   * video is exactly what `VideoPlayer` draws the placeholder and the error
   * chip for.
   */
  const ready = readyAttachments(message.attachments);
  const images = ready.filter((a) => a.kind === "image");
  const imageUrls = images.map((a) => a.url);
  const gridUrls = images.map((a) => a.renditions?.grid ?? a.url);
  // A video is played in the bubble now — with controls, and never on its own
  // (OSO-437). It used to be offered as a file chip because there was no
  // player; there is one, and it falls back to exactly that chip when the
  // browser cannot decode the file.
  //
  // Drawn from ALL the attachments, not the ready ones: a clip the worker is
  // still encoding is what the placeholder is for (OSO-490), and only the
  // sender is ever handed one.
  const videos = videoAttachments(message.attachments);

  /**
   * NOT DELIVERED (OSO-490 §5). `publishedAt === null` means the message is
   * held back for a video: it is not in the recipient's thread, not their last
   * inbox line and not in their unread count. The SENDER keeps their own
   * bubble, with the reaction and reply affordances withheld — there is nobody
   * on the other end of them.
   *
   * THE FAILED STATE NOW HAS A WAY OUT, AND IT IS DELETE (OSO-564). A message
   * still cannot be EDITED, so the recovery edit §2 gives a post — drop the
   * clip, publish the rest — has no equivalent here: what the post keeps is
   * its body, and a message that loses its body is nothing. So the offer is
   * the whole message, from two places that mean the same thing: the error
   * chip's own button and the ⋮-less hover action beside the bubble.
   *
   * It is still not dimmed, and the sentence still does not promise "Sendes,
   * når videoen er klar": dimming and that wording are how the ENCODING case
   * says "come back later", and this state never resolves on its own.
   */
  const pending = message.publishedAt === null;
  const blockedByFailure =
    pending && message.attachments.some((a) => a.status === "failed");
  const awaitingVideo = pending && !blockedByFailure;

  const showReport = canReport && conversationId != null && !mine;
  /**
   * `can.delete` is the server's own answer — the author of a live, non-system
   * message — and is deliberately not recomputed from `isMine` here. It is
   * true for a message still held back by a transcode, which is the case the
   * route exists for.
   */
  const canDelete = message.can.delete;
  /**
   * `can.edit` is NARROWER than `can.delete` and is likewise the server's own
   * answer (OSO-607): the author of a live message that has been DELIVERED. A
   * message a transcode is still holding back says false, because rewriting
   * its text would not release it — deleting it is still the way out of that.
   *
   * `bodyMarkdown` rides on the message for exactly this, so opening the
   * editor costs no request. It is null whenever `can.edit` is false.
   */
  const canEdit = message.can.edit;
  // The picker needs the conversation for its custom-emoji library; `can.react`
  // is the server's own answer for whether this message can be reacted to at all.
  const canReact = message.can.react && conversationId != null;

  /** Seeds the editor from the message's own source — see `can.edit`. */
  const startEdit = () => {
    setDraft(saved?.bodyMarkdown ?? message.bodyMarkdown ?? "");
    setEditing(true);
  };

  const replyButton = message.can.quote && onReply && (
    <button
      type="button"
      onClick={() => onReply(message)}
      aria-label={`Svar på besked fra ${authorName}`}
      title="Svar"
      className="self-center shrink-0 p-1.5 rounded-lg text-subtle-foreground pointer-fine:lg:opacity-0 pointer-fine:lg:group-hover:opacity-100 pointer-fine:lg:focus-visible:opacity-100 hover:text-primary hover:bg-primary-muted transition-all"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 17 4 12 9 7" />
        <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
      </svg>
    </button>
  );

  const editButton = canEdit && !editing && (
    <button
      type="button"
      onClick={startEdit}
      aria-label="Redigér din besked"
      title="Redigér"
      className="self-center shrink-0 p-1.5 rounded-lg text-subtle-foreground pointer-fine:lg:opacity-0 pointer-fine:lg:group-hover:opacity-100 pointer-fine:lg:focus-visible:opacity-100 hover:text-primary hover:bg-primary-muted transition-all"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    </button>
  );

  const deleteButton = canDelete && (
    <button
      type="button"
      onClick={() => setConfirmDelete(true)}
      aria-label="Slet din besked"
      title="Slet"
      className="self-center shrink-0 p-1.5 rounded-lg text-subtle-foreground pointer-fine:lg:opacity-0 pointer-fine:lg:group-hover:opacity-100 pointer-fine:lg:focus-visible:opacity-100 hover:text-destructive hover:bg-destructive-muted transition-all"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    </button>
  );

  const reportButton = showReport && (
    <button
      type="button"
      onClick={() => setReportOpen(true)}
      aria-label={`Anmeld besked fra ${authorName}`}
      title="Anmeld denne besked"
      className="self-center shrink-0 p-1.5 rounded-lg text-subtle-foreground pointer-fine:lg:opacity-0 pointer-fine:lg:group-hover:opacity-100 pointer-fine:lg:focus-visible:opacity-100 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition-all"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </svg>
    </button>
  );

  const reactionButton = canReact && (
    <EmojiReactionPicker
      conversationId={conversationId}
      onSelect={(emoji) =>
        react.mutate(
          {
            messageId: message.id,
            emoji,
            // The pill the server already sent says whether this is mine, so
            // the upsert/delete choice is read, never recomputed.
            mine: message.reactions.some((r) => r.emoji === emoji && r.mine),
          },
          { onSuccess: (reactions) => onReacted?.(message.id, reactions) },
        )
      }
    >
      <button
        type="button"
        aria-label="Reager med emoji"
        title="Reager"
        className="self-center shrink-0 p-1.5 rounded-lg text-subtle-foreground pointer-fine:lg:opacity-0 pointer-fine:lg:group-hover:opacity-100 pointer-fine:lg:focus-visible:opacity-100 hover:text-warning hover:bg-warning-muted transition-all"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </button>
    </EmojiReactionPicker>
  );

  const replyRow = !pending && message.can.quote && onReply && (
    <ActionMenuItem onSelect={() => onReply(message)}>Svar</ActionMenuItem>
  );

  return (
    <div
      className={`group flex gap-2 ${mine ? "justify-end" : "justify-start"}${
        awaitingVideo ? " opacity-60" : ""
      }`}
      data-pending={pending || undefined}
      data-pending-reason={pending ? (blockedByFailure ? "failed" : "processing") : undefined}
    >
      {!mine && (
        <div className="w-8 shrink-0">
          {showAuthor && (
            <Avatar
              url={message.author?.avatar.small}
              name={authorName}
              size="sm"
            />
          )}
        </div>
      )}

      {/* On the viewer's own (right-aligned) messages, the hover actions sit
          to the LEFT of the bubble — mirroring where they land on everyone
          else's (left-aligned) messages, on the side away from the screen edge. */}
      {mine && (canDelete || !pending) && (
        <div ref={actionsRef} className="self-center flex items-center gap-0.5">
          {/* Nobody is on the other end of a reply or a reaction to a message
              that has not been delivered — but its author may still withdraw
              it, and on a failed transcode that is the only thing left to do. */}
          {!pending && reactionButton}
          <span className={HOVER_ROW_ONLY}>
            {!pending && replyButton}
            {/* `canEdit` is already false for an undelivered message, so this
                needs no `!pending` of its own — see the capability. */}
            {editButton}
            {deleteButton}
          </span>
          {(replyRow || (canEdit && !editing) || canDelete) && (
            <ActionMenu
              label="Handlinger for din besked"
              size="sm"
              align={menuAlign}
              className={COMPACT_ONLY}
              open={menuOpen}
              onOpenChange={handleMenuOpenChange}
            >
              {replyRow}
              {canEdit && !editing && (
                <ActionMenuItem onSelect={startEdit}>Redigér</ActionMenuItem>
              )}
              {canDelete && (
                <ActionMenuItem onSelect={() => setConfirmDelete(true)} destructive>
                  Slet
                </ActionMenuItem>
              )}
            </ActionMenu>
          )}
        </div>
      )}

      <div className={`max-w-[80%] min-w-0 ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {!mine && showAuthor && (
          <p className="text-xs font-medium text-muted-foreground mb-0.5 px-1 flex items-center gap-1">
            {authorName}
            {message.author?.isBot && (
              <>
                <BotBadge />
                <BotTranscriptButton messageId={message.id} />
              </>
            )}
          </p>
        )}

        {/* max-w-full: THE BUBBLE IS SHRINK-TO-FIT, and shrink-to-fit is
            floored at its MIN-CONTENT width — the widest chunk inside it with
            no break opportunity. Nothing about that floor is capped by the
            column's `max-w-[80%]`, so a bubble whose content has one wide
            unbreakable chunk grows past the column and, being right-aligned,
            hangs off the LEFT edge of the thread. Three ordinary messages do
            it: a quoted reply (QuotedMessage's `truncate` paragraphs are
            `white-space: nowrap`, so the FULL untruncated excerpt is one
            chunk), a long URL, and text pasted with non-breaking spaces.

            `min-w-0` used to stand here for the same reason and never worked:
            the automatic minimum size applies on a flex container's MAIN axis,
            and this is a flex-COLUMN item, so its `min-width` was already 0.
            That is how OSO-367 came back as OSO-487. Capping the width is what
            holds — `break-words` below then breaks the long chunk, and the
            quote ellipsizes as it was always meant to. */}
        <div
          className={`max-w-full rounded-2xl px-3.5 py-2 ${
            mine
              ? "theta-message-mine bg-indigo-600 text-white"
              : "bg-muted text-foreground"
          }`}
        >
          {message.replyToMessageId != null && (
            <QuotedMessage replyTo={message.replyTo} mine={mine} />
          )}

          {editing ? (
            <MessageEditor
              inputRef={draftRef}
              value={draft}
              onChange={setDraft}
              pending={edit.isPending}
              failed={edit.isError}
              onCancel={() => setEditing(false)}
              onSave={() => {
                const bodyMarkdown = draftRef.current?.getRaw() ?? draft;
                // A message with no words is still a message if it carries a
                // file — the same rule the composer and the route apply.
                if (
                  bodyMarkdown.trim() === "" &&
                  message.attachments.length === 0
                ) {
                  return;
                }
                edit.mutate(
                  { messageId: message.id, bodyMarkdown },
                  {
                    onSuccess: (updated) => {
                      setSaved(updated);
                      setEditing(false);
                    },
                  },
                );
              }}
            />
          ) : (
          <RichContent
            density="message"
            html={bodyHtml}
            images={imageUrls}
            gridImages={gridUrls}
            videos={videos.map((a) => ({
              url: a.url,
              hlsUrl: a.hlsUrl,
              filename: a.filename,
              posterUrl: a.posterUrl,
              width: a.width,
              height: a.height,
              status: a.status,
              /* "Fjern video" on a post is an EDIT that publishes the rest.
                 `PATCH /messages/{id}` cannot do that (OSO-607 does not touch
                 attachments, and a held-back message is a 409 there anyway),
                 so the only thing this chip can offer is the whole message,
                 and it says so. */
              onRemove: canDelete ? () => setConfirmDelete(true) : undefined,
              removeLabel: "Slet beskeden",
              removeBusy: remove.isPending,
            }))}
            onImageClick={imageUrls.length > 0 ? setLightboxIndex : undefined}
            className={`text-sm break-words [&_p]:my-0 [&_p+p]:mt-2 [&_a]:underline ${
              mine ? "[&_a]:text-white" : "[&_a]:text-indigo-600 dark:[&_a]:text-indigo-400"
            }`}
          />
          )}

          {/* Audio and files come from `attachments`: there is no token in the
              body for RichContent's strip pass to find (§1.12), so a voice
              note or a PDF would otherwise be sent and never rendered. These
              two `.map`s were here and nowhere else, which is how the same
              MP3 played in a DM and vanished from a group post; they are a
              shared component now so a fourth surface cannot be written
              without them. */}
          <AttachedFiles attachments={message.attachments} />
        </div>

        {message.reactions.length > 0 && (
          <div className="mt-1 px-1">
            <DmReactionSummary counts={message.reactions} messageId={message.id} />
          </div>
        )}

        {pending ? (
          <span
            className={`px-1 mt-0.5 text-xs ${
              blockedByFailure ? "text-destructive" : "text-subtle-foreground"
            }`}
          >
            {blockedByFailure
              ? "Videoen kunne ikke behandles, så beskeden blev ikke sendt. Slet den, og send igen."
              : "Sendes, når videoen er klar"}
          </span>
        ) : (
          (showTimestamp || editedAt) && (
            <span className="px-1 mt-0.5 flex items-center gap-1.5">
              {showTimestamp && <TimeAgo date={message.createdAt} />}
              {/* OSO-592 wrote this label and deliberately kept it OFF DMs,
                  because a message could not be edited and `editedAt` was
                  structurally always null here. OSO-607 makes it editable, so
                  the label follows — and it shows even where the run has
                  collapsed the timestamp, because "this is not what was sent"
                  is not something to hide behind a stamp. */}
              <EditedLabel at={editedAt} />
            </span>
          )
        )}

        {remove.isError && (
          <span className="px-1 mt-0.5 text-xs text-destructive">
            Beskeden kunne ikke slettes. Prøv igen.
          </span>
        )}
      </div>

      {!mine && !pending && (
        <div ref={actionsRef} className="self-center flex items-center gap-0.5">
          {/* Reaction first, as on my own messages: it is the one icon both
              layouts keep, so it sits in the same place in both. */}
          {reactionButton}
          <span className={HOVER_ROW_ONLY}>
            {replyButton}
            {/* Reports THIS message's author, rather than whoever the
                conversation happens to list first. */}
            {reportButton}
          </span>
          {(replyRow || showReport) && (
            <ActionMenu
              label={`Handlinger for besked fra ${authorName}`}
              size="sm"
              align={menuAlign}
              className={COMPACT_ONLY}
              open={menuOpen}
              onOpenChange={handleMenuOpenChange}
            >
              {replyRow}
              {showReport && (
                <ActionMenuItem onSelect={() => setReportOpen(true)}>Anmeld</ActionMenuItem>
              )}
            </ActionMenu>
          )}
        </div>
      )}

      {lightboxIndex !== null && images.length > 0 && (
        <ImageLightbox
          items={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          sidebar={<MessageLightboxSidebar message={message} />}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Slet beskeden?"
        description="Beskeden forsvinder for alle i samtalen. Det kan ikke fortrydes."
        confirmLabel="Slet"
        pending={remove.isPending}
        pendingLabel="Sletter…"
        onConfirm={() =>
          remove.mutate(message.id, { onSuccess: () => setConfirmDelete(false) })
        }
        onCancel={() => setConfirmDelete(false)}
      />

      {reportOpen && conversationId != null && (
        <ReportDialog
          conversationId={conversationId}
          userId={message.author?.id}
          defaultBlock={false}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
